class Knee {
  constructor([[x1, y1], [x2, y2], [x3, y3]], kneeOffset) {
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;
    this.x3 = x3;
    this.y3 = y3;

    const [kLeft, bLeft] = this.#calculateLinearParameters([
      [x1, y1],
      [x2, y2],
    ]);
    const [kRight, bRight] = this.#calculateLinearParameters([
      [x2, y2],
      [x3, y3],
    ]);
    this.leftLineFunction = (x) => kLeft * x + bLeft;
    this.rightLineFunction = (x) => kRight * x + bRight;

    const maxKneeOffset = Math.min(this.x2 - this.x1, this.x3 - this.x2) / 2;
    this.kneeOffset = kneeOffset > maxKneeOffset ? maxKneeOffset : kneeOffset;

    this.kneeLeftX = x2 - this.kneeOffset;
    this.kneeLeftY = this.leftLineFunction(this.kneeLeftX);
    this.kneeRightX = x2 + this.kneeOffset;
    this.kneeRightY = this.rightLineFunction(this.kneeRightX);

    const [kKnee, bKnee] = this.#calculateLinearParameters([
      [this.kneeLeftX, kLeft],
      [this.kneeRightX, kRight],
    ]);

    const cKnee =
      this.kneeLeftY -
      (kKnee / 2) * this.kneeLeftX * this.kneeLeftX -
      bKnee * this.kneeLeftX;

    this.kneeFunction = (x) => (kKnee / 2) * x * x + bKnee * x + cKnee;
  }

  #calculateLinearParameters([[x1, y1], [x2, y2]]) {
    const k = (y2 - y1) / (x2 - x1);
    const b = y1 - k * x1;
    return [k, b];
  }
}

class SoftKnee {
  softFunctionFactory(points, kneeOffset) {
    // check if the points set is bijective
    for (
      let previous = 0, current = 1;
      current < points.length;
      previous++, current++
    ) {
      if (points[previous][0] >= points[current][0]) {
        return;
      }
    }

    const knees = [];
    for (let left = 0, right = 2; right < points.length; left++, right++) {
      const kneePoints = points.slice(left, right + 1);
      knees.push(new Knee(kneePoints, kneeOffset));
    }

    return (x) => {
      // if x is before the left bound - return the left bound value
      if (x <= points[0][0]) {
        return points[0][1];
      }

      for (let i = 0; i < knees.length; i++) {
        const knee = knees[i];
        const nextKnee = knees[i + 1];

        // check if x belongs to the left line
        if (x > knee.x1 && x < knee.kneeLeftX) {
          return knee.leftLineFunction(x);
        }

        // check if x belongs to the soft knee
        if (x >= knee.kneeLeftX && x <= knee.kneeRightX) {
          return knee.kneeFunction(x);
        }

        // check if x belongs to the right line
        if (x > knee.kneeRightX && x < (nextKnee?.kneeLeftX ?? knee.x3)) {
          return knee.rightLineFunction(x);
        }
      }

      // if x is after the right bound - return the right bound value
      return points[points.length - 1][1];
    };
  }
}

const softKnee = new SoftKnee();
export { softKnee, SoftKnee };
